import { Alert, Box, Button, TextField } from "@mui/material";
import DOMPurify from "dompurify";
import { compiler } from "markdown-to-jsx/html";
import { useEffect, useState } from "react";
import { OverlaySlot } from "../components/Wrappers/OverlaySlot";
import { useThemeContext } from "../globalContext/global-context";
import { resolveSurveyLayout } from "../layout/surveyLayout";
import { HostOverlayProvider } from "../questions/hosts/shared/HostOverlay";
import type { AttemptDraftState } from "../questions/sessionSlot";
import { SessionSurveyHost } from "../questions/SessionSurveyHost";
import { SurveySession } from "../session/SurveySession";
import { SurveyCustomCss } from "../theme/SurveyCustomCss";
import { SurveyProgress } from "./SurveyProgress";
import { SurveyUtilityBar } from "./SurveyUtilityBar";

const readViewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export const SurveyAppShell = ({ session }: { session: SurveySession }) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const [viewport, setViewport] = useState(readViewport);
  const [draftState, setDraftState] = useState<AttemptDraftState | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentPending, setCommentPending] = useState(false);
  const layout = resolveSurveyLayout(theme.layout.orientation, viewport);

  useEffect(() => {
    const originalTitle = document.title;
    const updateTitle = () => {
      const question = session.getSnapshot().attempt?.question.question;
      const content =
        question && question.type !== "complete" ? question.content : "";
      const markup = compiler(content.replace(/\s+/g, " ").trim(), {
        disableParsingRawHTML: true,
      });
      const text = DOMPurify.sanitize(markup, {
        ALLOWED_TAGS: [],
        RETURN_DOM_FRAGMENT: true,
      }).textContent;
      document.title = text?.trim() || theme.copy.labels.surveyTitle;
    };
    updateTitle();
    const unsubscribe = session.subscribe(updateTitle);
    return () => {
      unsubscribe();
      document.title = originalTitle;
    };
  }, [session, theme.copy.labels.surveyTitle]);

  useEffect(() => {
    const update = () => setViewport(readViewport());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  const submitComment = async () => {
    if (!comment.trim() || commentPending) return;
    setCommentPending(true);
    setCommentError(null);
    try {
      await session.submitComment(comment);
      setComment("");
      setCommentOpen(false);
    } catch (error) {
      setCommentError(error instanceof Error ? error.message : String(error));
    } finally {
      setCommentPending(false);
    }
  };

  const closeComment = () => {
    if (commentPending) return;
    session.dismissCommentDraft(comment);
    setCommentOpen(false);
  };

  return (
    <HostOverlayProvider host={layout.host}>
      <Box
        data-testid="survey-app-shell"
        data-survey-root="true"
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100dvh",
        }}
      >
        <SurveyCustomCss />
        <SurveyUtilityBar
          session={session}
          onComment={() => {
            session.beginCommentDraft();
            setCommentOpen(true);
          }}
        />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SessionSurveyHost
            session={session}
            progress={<SurveyProgress session={session} />}
            host={layout.host}
            arrangement={layout.arrangement}
            draftState={draftState}
            setDraftState={setDraftState}
          />
        </Box>
        <OverlaySlot
          open={commentOpen}
          onClose={closeComment}
          ariaLabelledBy="comment-title"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: `${theme.tokens.spacing["3"]}px`,
            }}
          >
            <Box component="h2" id="comment-title" sx={{ margin: 0 }}>
              {theme.copy.actions.comment}
            </Box>
            {commentError && <Alert severity="error">{commentError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              multiline
              label={theme.copy.actions.comment}
              sx={{
                // Safari can paint the outline through a hidden legend.
                // https://github.com/mui/material-ui/issues/46891
                "& .MuiOutlinedInput-notchedOutline legend": {
                  visibility: "visible",
                },
              }}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: `${theme.tokens.spacing["2"]}px`,
              }}
            >
              <Button disabled={commentPending} onClick={closeComment}>
                {theme.copy.actions.cancel}
              </Button>
              <Button
                disabled={commentPending || !comment.trim()}
                onClick={() => void submitComment()}
              >
                {theme.copy.actions.send}
              </Button>
            </Box>
          </Box>
        </OverlaySlot>
      </Box>
    </HostOverlayProvider>
  );
};
